import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Database, Pause, Play, RotateCcw } from "lucide-react";

const REPLAY_ASSET_URL = "/synthetic-data/btcusdt-0231-0331.v1.json";
const WINDOW_DURATION_MS = 60 * 60 * 1000;
const PLAYBACK_DURATION_MS = 45_000;
const END_HOLD_MS = 1_800;
const BAR_COUNT = 60;
const COMMIT_INTERVAL_MS = 42;

type EncodedEvent = [number, number, number, 1 | -1];

type ReplayPayload = {
  version: number;
  symbol: string;
  startUtc: string;
  endUtc: string;
  resolutionSeconds: number;
  eventCount: number;
  priceScale: number;
  quantityScale: number;
  basePrice: number;
  events: EncodedEvent[];
};

type ReplayEvent = {
  elapsedMs: number;
  price: number;
  quantity: number;
  side: 1 | -1;
};

type WorkingBar = {
  minute: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  notional: number;
  buyVolume: number;
  sellVolume: number;
  delta: number;
  tradeCount: number;
  buyTradeCount: number;
  sellTradeCount: number;
  cvd: number;
};

type MinuteBar = Omit<WorkingBar, "notional"> & {
  vwap: number;
  volumeImbalance: number;
  tradeCountImbalance: number;
};

type ReplayData = {
  payload: ReplayPayload;
  events: ReplayEvent[];
  fullBars: MinuteBar[];
};

type ReplaySnapshot = {
  bars: Array<MinuteBar | null>;
  elapsedMs: number;
  eventIndex: number;
  playhead: number;
};

type ChartProps = {
  activeIndex: number;
  bars: Array<MinuteBar | null>;
  fullBars: MinuteBar[];
};

const priceFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

function emptyBars() {
  return Array.from<MinuteBar | null>({ length: BAR_COUNT }).fill(null);
}

function emptySnapshot(): ReplaySnapshot {
  return { bars: emptyBars(), elapsedMs: 0, eventIndex: 0, playhead: 0 };
}

function decodeEvents(payload: ReplayPayload) {
  let elapsedMs = 0;
  let priceUnits = payload.basePrice;

  return payload.events.map(([timeDelta, priceDelta, quantityUnits, side]) => {
    elapsedMs += timeDelta;
    priceUnits += priceDelta;
    return {
      elapsedMs,
      price: priceUnits / payload.priceScale,
      quantity: quantityUnits / payload.quantityScale,
      side,
    } satisfies ReplayEvent;
  });
}

function applyEvent(
  bars: Array<WorkingBar | null>,
  event: ReplayEvent,
  cumulativeDelta: number,
) {
  const minute = Math.max(0, Math.min(BAR_COUNT - 1, Math.floor(event.elapsedMs / 60_000)));
  const signedQuantity = event.quantity * event.side;
  const nextCumulativeDelta = cumulativeDelta + signedQuantity;
  let bar = bars[minute];

  if (!bar) {
    bar = {
      minute,
      open: event.price,
      high: event.price,
      low: event.price,
      close: event.price,
      volume: 0,
      notional: 0,
      buyVolume: 0,
      sellVolume: 0,
      delta: 0,
      tradeCount: 0,
      buyTradeCount: 0,
      sellTradeCount: 0,
      cvd: nextCumulativeDelta,
    };
    bars[minute] = bar;
  }

  bar.high = Math.max(bar.high, event.price);
  bar.low = Math.min(bar.low, event.price);
  bar.close = event.price;
  bar.volume += event.quantity;
  bar.notional += event.price * event.quantity;
  bar.tradeCount += 1;
  bar.cvd = nextCumulativeDelta;

  if (event.side > 0) {
    bar.buyVolume += event.quantity;
    bar.buyTradeCount += 1;
  } else {
    bar.sellVolume += event.quantity;
    bar.sellTradeCount += 1;
  }

  bar.delta = bar.buyVolume - bar.sellVolume;
  return nextCumulativeDelta;
}

function finalizeBar(bar: WorkingBar): MinuteBar {
  return {
    minute: bar.minute,
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
    volume: bar.volume,
    buyVolume: bar.buyVolume,
    sellVolume: bar.sellVolume,
    delta: bar.delta,
    tradeCount: bar.tradeCount,
    buyTradeCount: bar.buyTradeCount,
    sellTradeCount: bar.sellTradeCount,
    cvd: bar.cvd,
    vwap: bar.volume > 0 ? bar.notional / bar.volume : bar.close,
    volumeImbalance: bar.volume > 0 ? bar.delta / bar.volume : 0,
    tradeCountImbalance: bar.tradeCount > 0
      ? (bar.buyTradeCount - bar.sellTradeCount) / bar.tradeCount
      : 0,
  };
}

function finalizeBars(bars: Array<WorkingBar | null>) {
  return bars.map((bar) => bar ? finalizeBar(bar) : null);
}

function aggregateAll(events: ReplayEvent[]) {
  const bars = Array.from<WorkingBar | null>({ length: BAR_COUNT }).fill(null);
  let cumulativeDelta = 0;
  events.forEach((event) => {
    cumulativeDelta = applyEvent(bars, event, cumulativeDelta);
  });
  return finalizeBars(bars).filter((bar): bar is MinuteBar => bar !== null);
}

function formatPrice(value: number | undefined) {
  return value === undefined ? "—" : priceFormatter.format(value);
}

function formatNumber(value: number | undefined, suffix = "") {
  return value === undefined ? "—" : `${compactFormatter.format(value)}${suffix}`;
}

function formatSigned(value: number | undefined, suffix = "") {
  if (value === undefined) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${compactFormatter.format(value)}${suffix}`;
}

function formatMinute(minute: number) {
  const totalMinutes = 2 * 60 + 31 + minute;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function formatPlaybackClock(elapsedMs: number) {
  const totalSeconds = 2 * 60 * 60 + 31 * 60 + Math.floor(elapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function OhlcvChart({ activeIndex, bars, fullBars }: ChartProps) {
  const left = 48;
  const right = 704;
  const priceTop = 12;
  const priceBottom = 166;
  const volumeTop = 188;
  const volumeBottom = 226;
  const plotWidth = right - left;
  const step = plotWidth / BAR_COUNT;
  const candleWidth = Math.max(3, step * 0.58);
  const minimum = Math.min(...fullBars.map((bar) => bar.low));
  const maximum = Math.max(...fullBars.map((bar) => bar.high));
  const pricePadding = Math.max(1, (maximum - minimum) * 0.06);
  const priceMin = minimum - pricePadding;
  const priceMax = maximum + pricePadding;
  const priceSpan = Math.max(1, priceMax - priceMin);
  const maxVolume = Math.max(...fullBars.map((bar) => bar.volume), 1);
  const x = (index: number) => left + (index + 0.5) * step;
  const y = (price: number) => priceTop + ((priceMax - price) / priceSpan) * (priceBottom - priceTop);
  const activeX = x(Math.max(0, activeIndex));
  const gridPrices = Array.from({ length: 4 }, (_, index) => priceMax - (priceSpan * index) / 3);

  return (
    <div
      aria-label="Animated BTCUSDT one-minute OHLCV bars with a replay-driven crosshair."
      className="data-evidence__chart"
      role="img"
    >
      <svg aria-hidden="true" preserveAspectRatio="xMidYMid meet" viewBox="0 0 720 246">
        <g className="data-evidence__grid">
          {gridPrices.map((price) => (
            <g key={price}>
              <line x1={left} x2={right} y1={y(price)} y2={y(price)} />
              <text x="6" y={y(price) + 3}>{priceFormatter.format(price)}</text>
            </g>
          ))}
          {[0, 15, 30, 45, 60].map((minute) => {
            const axisX = left + (minute / BAR_COUNT) * plotWidth;
            return <line key={minute} x1={axisX} x2={axisX} y1={priceTop} y2={volumeBottom} />;
          })}
        </g>

        <line className="data-evidence__pane-rule" x1={left} x2={right} y1="178" y2="178" />

        <g className="data-evidence__candles">
          {bars.map((bar, index) => {
            if (!bar) return null;
            const rising = bar.close >= bar.open;
            const bodyTop = Math.min(y(bar.open), y(bar.close));
            const bodyHeight = Math.max(1.5, Math.abs(y(bar.open) - y(bar.close)));
            return (
              <g className={index === activeIndex ? "is-active" : undefined} key={bar.minute}>
                <line className={rising ? "is-rising" : "is-falling"} x1={x(index)} x2={x(index)} y1={y(bar.high)} y2={y(bar.low)} />
                <rect
                  className={rising ? "is-rising" : "is-falling"}
                  height={bodyHeight}
                  rx="0.8"
                  width={candleWidth}
                  x={x(index) - candleWidth / 2}
                  y={bodyTop}
                />
                <rect
                  className={rising ? "data-evidence__volume is-rising" : "data-evidence__volume is-falling"}
                  height={(bar.volume / maxVolume) * (volumeBottom - volumeTop)}
                  width={candleWidth}
                  x={x(index) - candleWidth / 2}
                  y={volumeBottom - (bar.volume / maxVolume) * (volumeBottom - volumeTop)}
                />
              </g>
            );
          })}
        </g>

        {activeIndex >= 0 && <line className="data-evidence__crosshair" x1={activeX} x2={activeX} y1={priceTop} y2={volumeBottom} />}

        <g className="data-evidence__time-axis">
          {[0, 15, 30, 45, 60].map((minute) => (
            <text key={minute} textAnchor={minute === 0 ? "start" : minute === 60 ? "end" : "middle"} x={left + (minute / BAR_COUNT) * plotWidth} y="242">
              {formatMinute(minute)}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}

function OrderFlowChart({ activeIndex, bars, fullBars }: ChartProps) {
  const left = 48;
  const right = 704;
  const flowTop = 16;
  const flowBase = 82;
  const cvdTop = 146;
  const cvdBottom = 218;
  const plotWidth = right - left;
  const step = plotWidth / BAR_COUNT;
  const barWidth = Math.max(3, step * 0.6);
  const maxSideVolume = Math.max(...fullBars.flatMap((bar) => [bar.buyVolume, bar.sellVolume]), 1);
  const cvdMinimum = Math.min(...fullBars.map((bar) => bar.cvd));
  const cvdMaximum = Math.max(...fullBars.map((bar) => bar.cvd));
  const cvdSpan = Math.max(1, cvdMaximum - cvdMinimum);
  const x = (index: number) => left + (index + 0.5) * step;
  const cvdY = (value: number) => cvdTop + ((cvdMaximum - value) / cvdSpan) * (cvdBottom - cvdTop);
  const populated = bars.filter((bar): bar is MinuteBar => bar !== null);
  const cvdPath = populated.map((bar, index) => `${index === 0 ? "M" : "L"}${x(bar.minute).toFixed(2)} ${cvdY(bar.cvd).toFixed(2)}`).join(" ");
  const activeX = x(Math.max(0, activeIndex));

  return (
    <div
      aria-label="Animated BTCUSDT buy and sell volume with window cumulative volume delta and a replay-driven crosshair."
      className="data-evidence__chart"
      role="img"
    >
      <svg aria-hidden="true" preserveAspectRatio="xMidYMid meet" viewBox="0 0 720 246">
        <g className="data-evidence__grid">
          <line x1={left} x2={right} y1={flowBase} y2={flowBase} />
          <line x1={left} x2={right} y1={cvdTop} y2={cvdTop} />
          <line x1={left} x2={right} y1={cvdBottom} y2={cvdBottom} />
          {[0, 15, 30, 45, 60].map((minute) => {
            const axisX = left + (minute / BAR_COUNT) * plotWidth;
            return <line key={minute} x1={axisX} x2={axisX} y1={flowTop} y2={cvdBottom} />;
          })}
          <text x="6" y="21">BUY</text>
          <text x="6" y="139">SELL</text>
          <text x="6" y="159">CVD</text>
        </g>

        <g className="data-evidence__flow-bars">
          {bars.map((bar, index) => {
            if (!bar) return null;
            const buyHeight = (bar.buyVolume / maxSideVolume) * (flowBase - flowTop - 5);
            const sellHeight = (bar.sellVolume / maxSideVolume) * (flowBase - flowTop - 5);
            return (
              <g className={index === activeIndex ? "is-active" : undefined} key={bar.minute}>
                <rect className="is-buy" height={buyHeight} rx="0.8" width={barWidth} x={x(index) - barWidth / 2} y={flowBase - buyHeight} />
                <rect className="is-sell" height={sellHeight} rx="0.8" width={barWidth} x={x(index) - barWidth / 2} y={flowBase + 1} />
              </g>
            );
          })}
        </g>

        <path className="data-evidence__cvd-line" d={cvdPath} />
        {activeIndex >= 0 && <line className="data-evidence__crosshair" x1={activeX} x2={activeX} y1={flowTop} y2={cvdBottom} />}

        <g className="data-evidence__time-axis">
          {[0, 15, 30, 45, 60].map((minute) => (
            <text key={minute} textAnchor={minute === 0 ? "start" : minute === 60 ? "end" : "middle"} x={left + (minute / BAR_COUNT) * plotWidth} y="242">
              {formatMinute(minute)}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}

function MetricGrid({ metrics }: { metrics: Array<[string, string]> }) {
  return (
    <dl className="data-evidence__metrics">
      {metrics.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const schemaGroups = [
  ["Price", "open · high · low · close · vwap"],
  ["Participation", "volume · trade count · average size · maximum size"],
  ["Order flow", "buy/sell volume · delta · trade counts · imbalances · cvd"],
  ["Risk", "return · realized volatility · high–low range"],
] as const;

export function DataEvidenceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const workingBarsRef = useRef<Array<WorkingBar | null>>(Array.from({ length: BAR_COUNT }, () => null));
  const eventCursorRef = useRef(0);
  const cumulativeDeltaRef = useRef(0);
  const playheadRef = useRef(0);
  const hasAutoplayedRef = useRef(false);
  const [data, setData] = useState<ReplayData | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [snapshot, setSnapshot] = useState<ReplaySnapshot>(emptySnapshot);
  const [playing, setPlaying] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [mobileLens, setMobileLens] = useState<"ohlcv" | "flow">("ohlcv");
  const [inView, setInView] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(document.visibilityState === "visible");
  const [reducedMotion, setReducedMotion] = useState(false);

  const resetWorkingReplay = useCallback(() => {
    workingBarsRef.current = Array.from<WorkingBar | null>({ length: BAR_COUNT }).fill(null);
    eventCursorRef.current = 0;
    cumulativeDeltaRef.current = 0;
    playheadRef.current = 0;
    setSnapshot(emptySnapshot());
    setCompleted(false);
  }, []);

  const commitWorkingReplay = useCallback((elapsedMs: number, eventIndex: number, playhead: number) => {
    setSnapshot({
      bars: finalizeBars(workingBarsRef.current),
      elapsedMs,
      eventIndex,
      playhead,
    });
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => {
      setInView(Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.12));
    }, { threshold: [0, 0.12, 0.4] });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setDocumentVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetch(REPLAY_ASSET_URL, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Replay asset returned ${response.status}`);
        return response.json() as Promise<ReplayPayload>;
      })
      .then((payload) => {
        if (payload.version !== 1 || payload.eventCount !== payload.events.length) {
          throw new Error("Replay asset failed validation");
        }
        const events = decodeEvents(payload);
        const fullBars = aggregateAll(events);
        if (fullBars.length !== BAR_COUNT) throw new Error("Replay window is incomplete");
        setData({ payload, events, fullBars });
        resetWorkingReplay();
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if ((error as { name?: string }).name === "AbortError") return;
        console.error("Failed to load the synthetic BTCUSDT replay.", error);
        setStatus("error");
      });
    return () => controller.abort();
  }, [loadAttempt, resetWorkingReplay]);

  useEffect(() => {
    if (!data || status !== "ready") return;
    const frame = window.requestAnimationFrame(() => {
      if (reducedMotion) {
        playheadRef.current = 1;
        setPlaying(false);
        setCompleted(true);
        setSnapshot({
          bars: data.fullBars,
          elapsedMs: WINDOW_DURATION_MS,
          eventIndex: data.events.length,
          playhead: 1,
        });
        return;
      }
      if (inView && documentVisible && !hasAutoplayedRef.current) {
        hasAutoplayedRef.current = true;
        setPlaying(true);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [data, documentVisible, inView, reducedMotion, status]);

  useEffect(() => {
    if (!data || !playing || !inView || !documentVisible || reducedMotion) return;
    let frame = 0;
    let lastCommit = 0;
    const startedAt = performance.now() - playheadRef.current * PLAYBACK_DURATION_MS;

    const animate = (now: number) => {
      const playhead = Math.min(1, (now - startedAt) / PLAYBACK_DURATION_MS);
      const targetElapsed = playhead * WINDOW_DURATION_MS;
      let eventIndex = eventCursorRef.current;
      let cumulativeDelta = cumulativeDeltaRef.current;

      while (eventIndex < data.events.length) {
        const event = data.events[eventIndex];
        if (!event || event.elapsedMs > targetElapsed) break;
        cumulativeDelta = applyEvent(workingBarsRef.current, event, cumulativeDelta);
        eventIndex += 1;
      }

      eventCursorRef.current = eventIndex;
      cumulativeDeltaRef.current = cumulativeDelta;
      playheadRef.current = playhead;

      if (now - lastCommit >= COMMIT_INTERVAL_MS || playhead === 1) {
        lastCommit = now;
        commitWorkingReplay(targetElapsed, eventIndex, playhead);
      }

      if (playhead === 1) {
        setPlaying(false);
        setCompleted(true);
        return;
      }
      frame = window.requestAnimationFrame(animate);
    };

    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, [commitWorkingReplay, data, documentVisible, inView, playing, reducedMotion]);

  useEffect(() => {
    if (!completed || !data || !inView || !documentVisible || reducedMotion) return;
    const timer = window.setTimeout(() => {
      resetWorkingReplay();
      setPlaying(true);
    }, END_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [completed, data, documentVisible, inView, reducedMotion, resetWorkingReplay]);

  const currentIndex = useMemo(() => {
    for (let index = snapshot.bars.length - 1; index >= 0; index -= 1) {
      if (snapshot.bars[index]) return index;
    }
    return 0;
  }, [snapshot.bars]);

  const activeIndex = currentIndex;
  const activeBar = snapshot.bars[activeIndex] ?? null;
  const progressStyle = { "--replay-progress": snapshot.playhead } as CSSProperties;

  const handleReplay = () => {
    if (!data || reducedMotion) return;
    resetWorkingReplay();
    setPlaying(true);
  };

  const handlePlayPause = () => {
    if (!data || reducedMotion) return;
    if (completed || snapshot.playhead >= 1) {
      handleReplay();
      return;
    }
    setPlaying((value) => !value);
  };

  const ohlcvMetrics: Array<[string, string]> = [
    ["Minute", `${formatMinute(activeIndex)} UTC`],
    ["Open", formatPrice(activeBar?.open)],
    ["High", formatPrice(activeBar?.high)],
    ["Low", formatPrice(activeBar?.low)],
    ["Close", formatPrice(activeBar?.close)],
    ["Volume", formatNumber(activeBar?.volume, " BTC")],
    ["VWAP", formatPrice(activeBar?.vwap)],
    ["Trades", activeBar ? activeBar.tradeCount.toLocaleString("en-US") : "—"],
  ];

  const flowMetrics: Array<[string, string]> = [
    ["Minute", `${formatMinute(activeIndex)} UTC`],
    ["Buy volume", formatNumber(activeBar?.buyVolume, " BTC")],
    ["Sell volume", formatNumber(activeBar?.sellVolume, " BTC")],
    ["Delta", formatSigned(activeBar?.delta, " BTC")],
    ["Window CVD", formatSigned(activeBar?.cvd, " BTC")],
    ["Volume imbalance", activeBar ? formatSigned(activeBar.volumeImbalance * 100, "%") : "—"],
    ["Buy trades", activeBar ? activeBar.buyTradeCount.toLocaleString("en-US") : "—"],
    ["Sell trades", activeBar ? activeBar.sellTradeCount.toLocaleString("en-US") : "—"],
  ];

  return (
    <section className="chapter data-section data-evidence" id="data" ref={sectionRef}>
      <div className="chapter-frame data-evidence__frame">
        <header className="data-evidence__intro">
          <div>
            <span>05 / Data</span>
            <h2>One hour. Every underlying pressure.</h2>
          </div>
          <p>Watch a synthetic event stream resolve into inspectable one-minute bars, then interrogate the participation behind every move.</p>
        </header>

        <dl className="data-evidence__proof" aria-label="Replay provenance">
          <div><dt>Instrument</dt><dd>BTCUSDT</dd></div>
          <div><dt>Replay source</dt><dd>29,485 events</dd></div>
          <div><dt>Resolution</dt><dd>60 × 1m bars</dd></div>
          <div><dt>Window</dt><dd>02:31–03:31 UTC</dd></div>
        </dl>

        {status !== "ready" || !data ? (
          <div className={`data-evidence__state data-evidence__state--${status}`} role={status === "error" ? "alert" : "status"}>
            <Database aria-hidden="true" size={22} />
            <div>
              <strong>{status === "error" ? "The illustrative replay could not be loaded." : "Preparing the one-hour replay…"}</strong>
              <p>{status === "error" ? "The section can still explain the dataset, but the event-driven proof is unavailable." : "Decoding 29,485 synthetic events into sixty one-minute bars."}</p>
            </div>
            {status === "error" && <button onClick={() => { setStatus("loading"); setLoadAttempt((attempt) => attempt + 1); }}>Retry replay</button>}
          </div>
        ) : (
          <>
            <div className="data-evidence__replay-bar">
              <div className="data-evidence__transport">
                <button aria-label={playing ? "Pause synthetic replay" : "Play synthetic replay"} disabled={reducedMotion} onClick={handlePlayPause}>
                  {playing ? <Pause aria-hidden="true" size={14} /> : <Play aria-hidden="true" size={14} />}
                </button>
                <button aria-label="Replay synthetic hour from the beginning" disabled={reducedMotion} onClick={handleReplay}>
                  <RotateCcw aria-hidden="true" size={14} />
                </button>
              </div>
              <div
                aria-label="Synthetic replay progress"
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={Math.round(snapshot.playhead * 100)}
                className="data-evidence__progress"
                role="progressbar"
                style={progressStyle}
              ><span /></div>
              <div className="data-evidence__clock">
                <span>{formatPlaybackClock(snapshot.elapsedMs)} UTC</span>
                <span>{snapshot.eventIndex.toLocaleString("en-US")} / {data.payload.eventCount.toLocaleString("en-US")} events</span>
              </div>
            </div>

            <div aria-label="Data view" className="data-evidence__mobile-tabs">
              <button aria-pressed={mobileLens === "ohlcv"} onClick={() => setMobileLens("ohlcv")}>OHLCV</button>
              <button aria-pressed={mobileLens === "flow"} onClick={() => setMobileLens("flow")}>Order flow</button>
            </div>

            <div className="data-evidence__planes" data-mobile-lens={mobileLens}>
              <article className="data-evidence__plane data-evidence__plane--ohlcv">
                <header>
                  <div><span>PRICE / 1M</span><h3>OHLCV formation</h3><p>Event-built bars with volume and VWAP</p></div>
                  <strong>{activeBar ? formatPrice(activeBar.close) : "—"}</strong>
                </header>
                <OhlcvChart activeIndex={activeIndex} bars={snapshot.bars} fullBars={data.fullBars} />
                <MetricGrid metrics={ohlcvMetrics} />
              </article>

              <article className="data-evidence__plane data-evidence__plane--flow">
                <header>
                  <div><span>ORDER FLOW / 1M</span><h3>Participation context</h3><p>Buy/sell volume, delta, trade count, and CVD</p></div>
                  <strong className={activeBar && activeBar.delta < 0 ? "is-negative" : "is-positive"}>{formatSigned(activeBar?.delta)}</strong>
                </header>
                <OrderFlowChart activeIndex={activeIndex} bars={snapshot.bars} fullBars={data.fullBars} />
                <MetricGrid metrics={flowMetrics} />
              </article>
            </div>

            <section className="data-evidence__schema" aria-labelledby="data-schema-title">
              <header><div><h3 id="data-schema-title">Derived field ledger</h3><p>Twenty-four inspectable bar fields—not an inflated feature matrix.</p></div><span>24 FIELDS</span></header>
              <div>
                {schemaGroups.map(([group, fields]) => (
                  <div key={group}><strong>{group}</strong><p>{fields}</p></div>
                ))}
              </div>
            </section>
          </>
        )}

        <p className="data-evidence__disclaimer">Synthetic data for illustrative purposes. Not market data or investment advice. The replay demonstrates bar construction and does not imply that raw tick access is included in the beta.</p>
      </div>
    </section>
  );
}
